import { FC, useEffect, useState, useMemo } from 'react';
import { Select, Typography, Slider, Row, InputNumber, Col, Card } from 'antd';
import { Box, BoxConfig } from '@ant-design/plots';
import Plot from 'react-plotly.js';
import { DiskData, MergedFspSegment } from '../../types/turnFsp';
import { DEFAULT_PLOTLY_CONFIG, DEFAULT_PLOTLY_LAYOUT, DISK_COLORS } from '../../utils/constants/constants';
import { Shape } from 'plotly.js';

const { Title } = Typography;

export enum DIAMETER_DETECTION_METHOD {
  minMaxSignal = 'minMaxSignal',
  zeroDerivative = 'zeroDerivative',
  minMaxDerivative = 'minMaxDerivative',
}

interface Props {
  mergedData: MergedFspSegment[];
  peptide: number; // example: 24
  setComputedDiameters: React.Dispatch<React.SetStateAction<DiskData[]>>;
}

interface CrossingInfo {
  angle: number;
  type: 'start' | 'stop' | 'unknown';
}

export const LapSelectorPlot: FC<Props> = ({ mergedData, peptide, setComputedDiameters }) => {
  const [removedLaps, setRemovedLaps] = useState<number[]>([0]);
  const [signalSmoothing, setSignalSmoothing] = useState<number>(2); // rolling window size
  const [derivativeSmoothing, setDerivativeSmoothing] = useState<number>(2); // rolling window size
  const [plotlyDataMedian, setPlotlyDataMedian] = useState<Plotly.Data[]>([]);
  const [plotlyDataDerivative, setPlotlyDataDerivative] = useState<Plotly.Data[]>([]);
  const [plotlyLayout, setPlotlyLayout] = useState<Partial<Plotly.Layout>>({});
  const [plotlyConfig, setPlotlyConfig] = useState<Partial<Plotly.Config>>({});
  const [AC, setAC] = useState<number>(26); // default AC value
  const [angleOffset, setAngleOffset] = useState<number>(45);
  const [crossingResults, setCrossingResults] = useState<Record<number, CrossingInfo[]>>({});
  const [diameterMethod, setDiametedMethod] = useState<DIAMETER_DETECTION_METHOD>(DIAMETER_DETECTION_METHOD.zeroDerivative);

  const options = useMemo(
    () =>
      mergedData.map((seg) => ({
        label: `lap ${seg.turnNumberID}`,
        value: seg.turnNumberID,
      })),
    [mergedData]
  );

  const filtered = useMemo(() => mergedData.filter((seg) => !removedLaps.includes(seg.turnNumberID)), [mergedData, removedLaps]);

  const median = (arr: number[]): number => {
    if (!arr.length) return NaN;
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  };

  const computeDerivative = (x: number[], y: number[]): number[] => {
    const dydx: number[] = [];
    for (let i = 0; i < y.length - 1; i++) {
      const dx = x[i + 1] - x[i];
      const dy = y[i + 1] - y[i];
      dydx.push(dy / dx);
    }
    dydx.push(dydx[dydx.length - 1]); // keep same length
    return dydx;
  };

  const secondDerivativeNonUniform = (x: number[], y: number[]): number[] => {
    const n = y.length;
    const result: number[] = new Array(n).fill(0);

    for (let i = 1; i < n - 1; i++) {
      const dx1 = x[i] - x[i - 1];
      const dx2 = x[i + 1] - x[i];

      const term1 = (y[i] - y[i - 1]) / dx1;
      const term2 = (y[i + 1] - y[i]) / dx2;

      result[i] = (2 / (x[i + 1] - x[i - 1])) * (term2 - term1);
    }

    return result;
  };

  // console.log('mergedData, peptide, setComputedDiameters', mergedData, peptide, setComputedDiameters);

  // Apply rolling window smoothing
  const smoothArray = (arr: number[], windowSize: number): number[] => {
    const smoothed: number[] = [];
    const half = Math.floor(windowSize / 2);
    for (let i = 0; i < arr.length; i++) {
      const start = Math.max(0, i - half);
      const end = Math.min(arr.length, i + half + 1);
      const slice = arr.slice(start, end);
      const avg = slice.reduce((a, b) => a + b, 0) / slice.length;
      smoothed.push(avg);
    }
    return smoothed;
  };

  // Detect zero-crossings (sign change in derivative)
  const detectZeroCrossings = (angles: number[], derivative: number[], secondDerivative: number[]): CrossingInfo[] => {
    const crossings: CrossingInfo[] = [];
    for (let i = 1; i < derivative.length; i++) {
      if ((derivative[i - 1] <= 0 && derivative[i] > 0) || (derivative[i - 1] >= 0 && derivative[i] < 0)) {
        const angle = angles[i];
        const second = secondDerivative[i] ?? 0;
        let type: CrossingInfo['type'] = 'unknown';
        if (second < 0) type = 'start';
        if (second > 0) type = 'stop';
        crossings.push({ angle, type });
      }
    }
    return crossings;
  };

  // Detect zero-crossings (sign change in derivative)
  const detectLeftRightBoundsZero = (angles: number[], signal: number[], referenceAngle: number): CrossingInfo[] => {
    let crossings: CrossingInfo[] = [];

    let rightBound: number = 0;
    let leftBound: number = 0;
    let passedReferenceAngle: boolean = false;

    for (let i = 1; i < angles.length - 1; i++) {
      // helpers
      let d1 = signal[i - 1];
      let d2 = signal[i];
      let a1 = angles[i - 1];
      let a2 = angles[i];
      let isZeroCrossing = (d1 <= 0 && d2 > 0) || (d1 >= 0 && d2 < 0);

      // linear interpolation : d(t1+delta_t_0) = 0
      let delta_t_0 = d1 / (d1 - d2);

      // linear interpolation to find the corresponding angle a(t1 + delta_t_0)
      // valid hypothesis as the angles have been remapped on sensorgram timestamps
      let a_res = a1 + (a2 - a1) * delta_t_0;

      // compute left bound
      // detect zero crossing for second derivative if we are on left side of reference angle
      if (!passedReferenceAngle && isZeroCrossing) {
        leftBound = a_res;
      }

      // compute right bound
      // detect zero crossing for second derivative if we are on right side of reference angle
      if (passedReferenceAngle && isZeroCrossing) {
        rightBound = a_res;
        break;
      }

      // update
      if (a2 > referenceAngle) passedReferenceAngle = true;
    }

    crossings.push({ angle: leftBound, type: 'start' });
    crossings.push({ angle: rightBound, type: 'stop' });
    return crossings;
  };

  const detectLeftRightBoundsMinMax = (angles: number[], signal: number[], referenceAngle: number): CrossingInfo[] => {
    let crossings: CrossingInfo[] = [];

    const leftBoundReference = referenceAngle - 45;
    const rightBoundReference = referenceAngle + 45;

    let minVal: number | null = null;
    let maxVal: number | null = null;
    let minIdx: number = -1;
    let maxIdx: number = -1;

    for (let i = 0; i < angles.length; i++) {
      // if we are not searching in the right window
      if (angles[i] < leftBoundReference || angles[i] > rightBoundReference) continue;

      // initialise values
      if (minVal === null) {
        minVal = signal[i];
        minIdx = i;
      }
      if (maxVal === null) {
        maxVal = signal[i];
        maxIdx = i;
      }

      // update min and max indexes and values
      if (signal[i] < minVal) {
        minVal = signal[i];
        minIdx = i;
      } else if (signal[i] >= maxVal) {
        maxVal = signal[i];
        maxIdx = i;
      }
    }

    if (minIdx === -1 || maxIdx === -1) throw new Error("Couldn't compute min and max values");

    // derivative increasing
    if (angles[minIdx] < angles[maxIdx]) {
      crossings.push({ angle: angles[minIdx], type: 'start' });
      crossings.push({ angle: angles[maxIdx], type: 'stop' });
    } else {
      crossings.push({ angle: angles[maxIdx], type: 'start' });
      crossings.push({ angle: angles[minIdx], type: 'stop' });
    }
    return crossings;
  };

  function computeDiameter(alpha: number, AC: number): number {
    // alpha is in degrees
    const rad = (alpha / 2) * (Math.PI / 180); // convert half-angle to radians
    return 2 * AC * Math.sqrt(2 * (1 - Math.cos(rad)));
  }

  useEffect(() => {
    const medianTraces: Plotly.Data[] = [];
    const derivativeTraces: Plotly.Data[] = [];
    const annotations: Plotly.Layout['annotations'] = [];
    const results: Record<number, CrossingInfo[]> = {};
    console.log('computing traces for peptide', peptide, 'with derivative smoothing', derivativeSmoothing, filtered);

    // default disks positions : one disk every 90 degrees
    let referenceAngles = [0, 90, 180, 270];
    referenceAngles = referenceAngles.map((x) => x + angleOffset);

    filtered.forEach((seg) => {
      console.log('processing segment', seg.turnNumberID, seg.angleValues.length, Object.keys(seg.fspSensorData));
      const replicateKeys = Object.keys(seg.fspSensorData).filter((k) => k.startsWith(peptide.toString()));
      console.log('replicateKeys', replicateKeys);
      if (replicateKeys.length === 0) return;

      const nPoints = seg.angleValues.length;
      const yMedian: number[] = [];

      for (let i = 0; i < nPoints; i++) {
        const valuesAtI = replicateKeys.map((k) => seg.fspSensorData[k]?.[i]).filter((v) => v !== undefined);
        yMedian.push(median(valuesAtI));
      }

      const yMedianSmoothed = smoothArray(yMedian, signalSmoothing);

      const yDerivative = computeDerivative(seg.angleValues, yMedianSmoothed);
      const yDerivativeSmoothed = smoothArray(yDerivative, derivativeSmoothing);

      // const ySecondDerivative = computeDerivative(seg.angleValues, yDerivativeSmoothed);
      // const ySecondDerivative = secondDerivativeNonUniform(seg.angleValues, yMedianSmoothed);
      // const ySecondDerivativeSmoothed = ySecondDerivative
      // const ySecondDerivativeSmoothed = smoothArray(ySecondDerivative, derivativeSmoothing);
      // console.log('yMedian, yDerivativeSmoothed, ySecondDerivativeSmoothed', yMedian, yDerivativeSmoothed, ySecondDerivativeSmoothed);
      // Find crossings with classification

      let crossings: CrossingInfo[] = [];

      console.log('diameterMethod is', diameterMethod);
      switch (diameterMethod) {
        case DIAMETER_DETECTION_METHOD.minMaxDerivative:
          referenceAngles.forEach((v) => crossings.push(...detectLeftRightBoundsMinMax(seg.angleValues, yDerivativeSmoothed, v)));
          break;
        case DIAMETER_DETECTION_METHOD.minMaxSignal:
          referenceAngles.forEach((v) => crossings.push(...detectLeftRightBoundsMinMax(seg.angleValues, yMedianSmoothed, v)));
          break;
        case DIAMETER_DETECTION_METHOD.zeroDerivative:
          referenceAngles.forEach((v) => crossings.push(...detectLeftRightBoundsZero(seg.angleValues, yDerivativeSmoothed, v)));
          break;
        default:
          throw new Error('diameter Method not understood');
      }
      results[seg.turnNumberID] = crossings;
      console.log('crossings for lap', seg.turnNumberID, crossings);
      medianTraces.push({
        type: 'scatter',
        mode: 'lines+markers',
        x: seg.angleValues,
        y: yMedianSmoothed,
        name: `lap ${seg.turnNumberID}`,
      });

      derivativeTraces.push({
        type: 'scatter',
        mode: 'lines+markers',
        x: seg.angleValues,
        y: yDerivativeSmoothed,
        name: `lap ${seg.turnNumberID}`,
      });

      crossings.forEach((res_i, i) => {
        annotations.push({
          x: res_i.angle,
          y: 1, // use paper coordinates
          yref: 'paper', // stick to top of chart
          xref: 'x',
          text: '▼',
          font: { size: 18, color: DISK_COLORS[Math.floor(i / 2)] }, // 2 bounds (start and stop) for each disk
          showarrow: false,
          hovertext: `Lap ${seg.turnNumberID}, disk ${Math.floor(i / 2) + 1}:<br>${res_i.type} at ${res_i.angle}`,
          align: 'center',
          yshift: 8, // move slightly above axis
        });
      });
    });

    // draw vertical lines to track reference positions
    const shapes: Plotly.Layout['shapes'] = referenceAngles.map((x) => ({
      type: 'line',
      xref: 'x',
      yref: 'paper',
      x0: x,
      x1: x,
      y0: 0,
      y1: 1,
      line: {
        color: 'red',
        width: 2,
        dash: 'dash',
      },
    }));

    referenceAngles.forEach((x) => {
      const shape: Partial<Shape> = {
        type: 'rect',
        xref: 'x',
        yref: 'paper',
        x0: x - 45,
        x1: x + 45,
        y0: 0,
        y1: 1,
        fillcolor: 'lightblue',
        opacity: 0.3,
      };

      shapes.push(shape);
    });

    const config = { ...DEFAULT_PLOTLY_CONFIG };
    const layout: Partial<Plotly.Layout> = {
      ...DEFAULT_PLOTLY_LAYOUT,
      dragmode: 'zoom',
      datarevision: Math.random(),
      xaxis: { title: { text: 'Angle (deg)' }, automargin: true },
      yaxis: { title: { text: `Median FSP for peptide ${peptide}` }, automargin: true },
      shapes: shapes,
      annotations: annotations,
    };
    console.log('medianTraces', medianTraces);
    setPlotlyDataMedian(medianTraces);
    setPlotlyDataDerivative(derivativeTraces);
    setPlotlyLayout(layout);
    setPlotlyConfig(config);
    setCrossingResults(results);
  }, [filtered, peptide, signalSmoothing, derivativeSmoothing, angleOffset, diameterMethod]);

  useEffect(() => {
    console.log('crossingResults', crossingResults);

    console.log('🔎 Crossing results per lap:');

    const computedDiameters: DiskData[] = [];
    Object.entries(crossingResults).forEach(([lap, infos]) => {
      console.log(`Lap ${lap}:`, infos);

      if (infos.length === 8) {
        // expecting 4 disks as of September 2025
        for (let i = 0; i < infos.length; i += 2) {
          const start = infos[i];
          const end = infos[i + 1];
          if (start && end) {
            const alpha = end.angle - start.angle;
            const diameter = computeDiameter(alpha, AC);
            console.log(`   ➡️ Computed diameter for Lap ${lap}, disk ${i / 2 + 1} = ${diameter.toFixed(3)}`);
            computedDiameters.push({
              lap: parseInt(lap),
              disk: i / 2 + 1,
              diameter: diameter,
              angle_l: start.angle,
              angle_r: end.angle,
            });
          }
        }
      } else {
        console.warn(`⚠️ Lap ${lap} has ${infos.length} crossings (expected 8 for a full disk)`);
      }
    });

    setComputedDiameters(computedDiameters);
  }, [crossingResults, AC]);

  return (
    <div>
      {/* Controls row */}
      <Row gutter={16} style={{ marginBottom: 20, alignItems: 'center' }}>
        <Col>
          <Title level={5}>Laps to remove</Title>
          <Select mode="multiple" style={{ width: 300 }} placeholder="Select laps to remove" onChange={(vals) => setRemovedLaps(vals)} options={options} allowClear />
        </Col>
        <Col>
          <Title level={5}>Diameter processing method</Title>
          <Select
            style={{ width: 300 }}
            onChange={(val) => setDiametedMethod(val)}
            defaultValue={DIAMETER_DETECTION_METHOD.zeroDerivative}
            options={[
              { value: DIAMETER_DETECTION_METHOD.minMaxDerivative, label: ' min & max on derivative' },
              { value: DIAMETER_DETECTION_METHOD.zeroDerivative, label: 'zero on derivative signal' },
            ]}
          />
        </Col>

        <Col>
          <Title level={5}>Signal smoothing window size</Title>
          <Slider min={1} max={15} step={1} value={signalSmoothing} onChange={(val) => setSignalSmoothing(val as number)} tooltipVisible style={{ width: 300 }} />
        </Col>
        <Col>
          <Title level={5}>Derivative smoothing window size</Title>
          <Slider min={1} max={15} step={1} value={derivativeSmoothing} onChange={(val) => setDerivativeSmoothing(val as number)} tooltipVisible style={{ width: 300 }} />
        </Col>
      </Row>

      {/* Derivative plot  */}
      <Plot
        data={plotlyDataDerivative}
        layout={{
          ...plotlyLayout,
          yaxis: { title: { text: `d²FSP/dAngle for peptide ${peptide}` } },
        }}
        config={plotlyConfig}
        style={{ width: '95%', height: 400, margin: 'auto' }}
        useResizeHandler
      />

      {/* Median plot  */}
      <Plot data={plotlyDataMedian} layout={plotlyLayout} config={plotlyConfig} style={{ width: '95%', height: 400, margin: 'auto' }} useResizeHandler />
    </div>
  );
};
